import { isCaptchaRequired } from "./errors";

/**
 * Resolves a fresh Yandex SmartCaptcha token. The widget runs in the browser
 * only, so server components must NOT supply a provider — they should let the
 * captcha error propagate to a client-side fallback.
 *
 * Provider returns:
 *   - the token string, when the user solves the captcha
 *   - throws, when the user dismisses the widget or the network errors out
 */
export type CaptchaTokenProvider = () => Promise<string>;

/**
 * One-shot captcha-on-failure retry wrapper.
 *
 * Flow:
 *   1. Call `fn()` with no token.
 *   2. If it throws ApiError("4601") AND a provider exists, ask the provider
 *      for a fresh token (this surfaces the Yandex SmartCaptcha widget).
 *   3. Call `fn(token)` once more. Any further failure — including a second
 *      4601 — propagates to the caller; we deliberately do NOT loop, because
 *      a second 4601 means either a backend bug or an abusive client, and
 *      both deserve to surface a hard error rather than spin.
 *
 * If no provider is supplied (server-side render, or a context that didn't
 * mount the widget), the captcha error propagates untouched.
 */
export async function withCaptchaRetry<T>(
  fn: (captchaToken?: string) => Promise<T>,
  provider?: CaptchaTokenProvider,
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (!provider || !isCaptchaRequired(error)) throw error;
    const token = await provider();
    return await fn(token);
  }
}
